import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Contract,
  Contractor,
  Employee,
  deleteContract,
  getContractors,
  getContracts,
  getEmployees,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatContractRecordId,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';

function formatContractAddressCell(contract: Pick<Contract, 'address' | 'cadastral_number'>): string {
  const address = contract.address?.trim() ?? '';
  const cadastral = contract.cadastral_number?.trim() ?? '';
  if (address && cadastral) return `${address}; к.н.: ${cadastral}`;
  if (address) return address;
  if (cadastral) return `к.н.: ${cadastral}`;
  return '—';
}

type ContractSortKey =
  | 'id'
  | 'contract_number'
  | 'subject'
  | 'address'
  | 'contractor'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'contract_status'
  | 'total_amount';

type ContractsFilterMenuKey = 'number' | 'status' | 'contractor' | 'manager' | 'project_lead' | null;

function contractDetailPath(level: number, mainContractId: number, contractId: number): string {
  const targetId = level === 2 ? mainContractId : contractId;
  return `/contracts/${targetId}`;
}

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasDepartment } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ContractsFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterContractNumbers, setFilterContractNumbers] = useState<string[]>([]);
  const [subjectFilterText, setSubjectFilterText] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterContractorIds, setFilterContractorIds] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<ContractSortKey>>({
    key: 'total_amount',
    direction: 'desc',
  });

  const canOpenPage = hasDepartment('ALL_COMP');
  const canDelete = can('CONTRACTS', 'DELETE');
  const canSum = can('CONTRACT_SUM', 'VIEW');

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setContracts([]);
      return;
    }

    setIsLoading(true);
    try {
      const [contractsData, contractorsData, employeesData] = await Promise.all([
        getContracts(),
        getContractors(),
        getEmployees(),
      ]);
      setContracts(contractsData);
      setContractors(contractorsData);
      setEmployees(employeesData);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setContracts([]);
    }
  }, [canOpenPage, fetchData]);

  const getContractorName = useCallback(
    (id: number): string => contractors.find((c) => c.id === id)?.name || 'Неизвестно',
    [contractors],
  );

  const getEmployeeName = useCallback(
    (id?: number): string => {
      if (!id) {
        return '-';
      }
      return employees.find((employee) => employee.id === id)?.full_name || 'Неизвестно';
    },
    [employees],
  );

  const clearFilters = (): void => {
    setSearchText('');
    setFilterContractNumbers([]);
    setSubjectFilterText('');
    setFilterStatuses([]);
    setFilterContractorIds([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterContractNumbers.length > 0 ||
    subjectFilterText.trim() !== '' ||
    filterStatuses.length > 0 ||
    filterContractorIds.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    minAmountFilter.trim() !== '' ||
    maxAmountFilter.trim() !== '';

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const contractNumberFilterOptions = useMemo(() => {
    const set = new Set(contracts.map((c) => c.contract_number));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [contracts]);

  const statusFilterOptions = useMemo(() => {
    const set = new Set(contracts.map((c) => c.contract_status));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [contracts]);

  const contractorFilterOptions = useMemo(() => {
    const set = new Set(contracts.map((c) => String(c.contractor_id)));
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [contracts]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of contracts) {
      if (c.manager_id != null) {
        set.add(String(c.manager_id));
      }
    }
    return [...set].sort((a, b) => getEmployeeName(Number(a)).localeCompare(getEmployeeName(Number(b)), 'ru'));
  }, [contracts, getEmployeeName]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of contracts) {
      if (c.project_lead_id != null) {
        set.add(String(c.project_lead_id));
      }
    }
    return [...set].sort((a, b) => getEmployeeName(Number(a)).localeCompare(getEmployeeName(Number(b)), 'ru'));
  }, [contracts, getEmployeeName]);

  const filteredContracts = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return contracts.filter((contract) => {
      const contractorName = getContractorName(contract.contractor_id);
      const managerName = getEmployeeName(contract.manager_id);
      const leadName = getEmployeeName(contract.project_lead_id);

      const searchable = [
        contract.contract_number,
        contract.contract_subject,
        contractorName,
        managerName,
        leadName,
        contract.contract_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = Number(contract.total_amount) || 0;
      const minAmount = minAmountFilter ? Number(minAmountFilter) : Number.NEGATIVE_INFINITY;
      const maxAmount = maxAmountFilter ? Number(maxAmountFilter) : Number.POSITIVE_INFINITY;

      const matchesSearch = !text || searchable.includes(text);
      const subject = (contract.contract_subject || '').trim();
      const matchesNumber =
        filterContractNumbers.length === 0 || filterContractNumbers.includes(contract.contract_number);
      const matchesSubject =
        subjectFilterText.trim() === '' || subject.toLowerCase().includes(subjectFilterText.trim().toLowerCase());
      const matchesStatus = filterStatuses.length === 0 || filterStatuses.includes(contract.contract_status);
      const matchesContractor =
        filterContractorIds.length === 0 || filterContractorIds.includes(String(contract.contractor_id));
      const matchesManager =
        filterManagerIds.length === 0 || filterManagerIds.includes(String(contract.manager_id || ''));
      const matchesLead =
        filterLeadIds.length === 0 || filterLeadIds.includes(String(contract.project_lead_id || ''));
      const matchesAmount = amount >= minAmount && amount <= maxAmount;

      return (
        matchesSearch &&
        matchesNumber &&
        matchesSubject &&
        matchesStatus &&
        matchesContractor &&
        matchesManager &&
        matchesLead &&
        matchesAmount
      );
    });
  }, [
    contracts,
    searchText,
    filterContractNumbers,
    subjectFilterText,
    filterStatuses,
    filterContractorIds,
    filterManagerIds,
    filterLeadIds,
    minAmountFilter,
    maxAmountFilter,
    getContractorName,
    getEmployeeName,
  ]);

  const sortedContracts = useMemo(() => {
    const contractById = new Map(contracts.map((contract) => [contract.id, contract]));
    const supplementaryToMain = new Map<number, number>();
    for (const contract of contracts) {
      if ((contract.hierarchy_level ?? 1) !== 1) continue;
      for (const supplementaryId of contract.supplementary_contract_ids || []) {
        supplementaryToMain.set(supplementaryId, contract.id);
      }
    }

    const getContractSortValue = (
      contract: Contract,
      key: ContractSortKey,
    ): string | number | undefined => {
      const values: Record<ContractSortKey, string | number | undefined> = {
        id: contract.id,
        contract_number: contract.contract_number,
        subject: contract.contract_subject,
        address: formatContractAddressCell(contract),
        contractor: getContractorName(contract.contractor_id),
        manager: getEmployeeName(contract.manager_id),
        projectLead: getEmployeeName(contract.project_lead_id),
        start_date: contract.start_date,
        end_date: contract.end_date,
        contract_status: contract.contract_status,
        total_amount: contract.total_amount,
      };
      return values[key];
    };

    type MainGroup = {
      mainContractId: number;
      mainContract: Contract;
      level1Rows: Contract[];
      level2Rows: Contract[];
    };

    const groups = new Map<string, MainGroup>();

    for (const contract of filteredContracts) {
      const level = contract.hierarchy_level ?? 1;
      const mainContractId = level === 2 ? (supplementaryToMain.get(contract.id) ?? contract.id) : contract.id;
      const mainContract = contractById.get(mainContractId) ?? contract;
      const groupKey = String(mainContractId);
      let group = groups.get(groupKey);
      if (!group) {
        group = { mainContractId, mainContract, level1Rows: [], level2Rows: [] };
        groups.set(groupKey, group);
      }
      if (level === 1) {
        group.level1Rows.push(contract);
      } else {
        group.level2Rows.push(contract);
      }
    }

    const { key: sortKey, direction: sortDirection } = sortState;

    for (const group of groups.values()) {
      const sortRows = (rows: Contract[]): void => {
        rows.sort((left, right) =>
          compareValues(
            getContractSortValue(left, sortKey),
            getContractSortValue(right, sortKey),
            sortDirection,
          ),
        );
      };
      sortRows(group.level1Rows);
      sortRows(group.level2Rows);
    }

    const sortedGroups = [...groups.values()].sort((left, right) =>
      compareValues(
        getContractSortValue(left.mainContract, sortKey),
        getContractSortValue(right.mainContract, sortKey),
        sortDirection,
      ),
    );

    const result: Array<{
      contract: Contract;
      level: number;
      mainContractId: number;
      mainContractNumber: string;
    }> = [];

    for (const group of sortedGroups) {
      const mainContractNumber = group.mainContract.contract_number;
      for (const contract of [...group.level1Rows, ...group.level2Rows]) {
        const level = contract.hierarchy_level ?? 1;
        result.push({
          contract,
          level,
          mainContractId: group.mainContractId,
          mainContractNumber,
        });
      }
    }

    return result;
  }, [contracts, filteredContracts, sortState, getContractorName, getEmployeeName]);

  const totalFilteredAmount = useMemo(
    () => sortedContracts.reduce((sum, { contract }) => sum + (Number(contract.total_amount) || 0), 0),
    [sortedContracts],
  );

  const handleSort = (key: ContractSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const selectedContract = useMemo(
    () => (selectedContractId == null
      ? null
      : contracts.find((contract) => contract.id === selectedContractId) ?? null),
    [contracts, selectedContractId],
  );

  useEffect(() => {
    if (selectedContractId == null) {
      return;
    }
    if (!sortedContracts.some(({ contract }) => contract.id === selectedContractId)) {
      setSelectedContractId(null);
    }
  }, [sortedContracts, selectedContractId]);

  const handleDeleteSelected = async (): Promise<void> => {
    if (!selectedContract || isDeleting) {
      return;
    }

    const confirmed = window.confirm(
      `Удалить договор «${selectedContract.contract_number}»?`,
    );
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    setError('');
    try {
      await deleteContract(selectedContract.id);
      setContracts((current) => current.filter((contract) => contract.id !== selectedContract.id));
      setSelectedContractId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка при удалении договора';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="data-page contracts-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице договоров (требуется отдел ALL_COMP).</div>
      ) : (
        <>
      <div className="page-header-row">
        <h1>Договоры</h1>
        <div className="page-header-row__actions">
          {canDelete && selectedContract ? (
            <button
              type="button"
              className="delete-row-button"
              onClick={() => void handleDeleteSelected()}
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаление…' : 'Удалить'}
            </button>
          ) : null}
          <button
            type="button"
            className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
          >
            Фильтры
            <span className="filters-toggle-badge" aria-hidden />
          </button>
        </div>
        <div className="page-header-row__search">
          <input
            type="text"
            className="search-input"
            placeholder="Поиск по текстовым полям"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
        {filtersOpen && (
          <aside className="filters-panel">
            <div className="filters-header">
              <h2>Фильтры</h2>
              <button type="button" className="link-button" onClick={clearFilters}>
                Сбросить
              </button>
            </div>
            <div className="filters-grid">
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Номер договора"
                  options={contractNumberFilterOptions}
                  selected={filterContractNumbers}
                  onSelectedChange={setFilterContractNumbers}
                  formatOption={(v) => v}
                  isOpen={filterMenuOpen === 'number'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'number' : null)}
                />
              </div>
              <div className="filters-field">
                <label>
                  Предмет договора
                  <input
                    value={subjectFilterText}
                    onChange={(e) => setSubjectFilterText(e.target.value)}
                    placeholder="Поиск по предмету договора"
                  />
                </label>
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Статус"
                  options={statusFilterOptions}
                  selected={filterStatuses}
                  onSelectedChange={setFilterStatuses}
                  formatOption={(v) => v}
                  isOpen={filterMenuOpen === 'status'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'status' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Контрагент"
                  options={contractorFilterOptions}
                  selected={filterContractorIds}
                  onSelectedChange={setFilterContractorIds}
                  formatOption={(v) => getContractorName(Number(v))}
                  isOpen={filterMenuOpen === 'contractor'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Менеджер"
                  options={managerFilterOptions}
                  selected={filterManagerIds}
                  onSelectedChange={setFilterManagerIds}
                  formatOption={(v) => getEmployeeName(Number(v))}
                  isOpen={filterMenuOpen === 'manager'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="РП"
                  options={projectLeadFilterOptions}
                  selected={filterLeadIds}
                  onSelectedChange={setFilterLeadIds}
                  formatOption={(v) => getEmployeeName(Number(v))}
                  isOpen={filterMenuOpen === 'project_lead'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                />
              </div>
              <label className="filters-sum-block">
                <span className="filters-sum-block__title">Сумма</span>
                <div className="filters-sum-block__row">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="filters-sum-block__input"
                    placeholder="от"
                    value={minAmountFilter}
                    onChange={(e) => setMinAmountFilter(e.target.value)}
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    className="filters-sum-block__input"
                    placeholder="до"
                    value={maxAmountFilter}
                    onChange={(e) => setMaxAmountFilter(e.target.value)}
                  />
                </div>
              </label>
            </div>
          </aside>
        )}

        <section className="table-content">
          {isLoading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <div className="table-panel">
              <div className="table-scroll">
                <table className="data-table contracts-page__table">
                  <colgroup>
                    <col className="contracts-page__colgrid contracts-page__colgrid--id" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--contractor" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--number" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--subject" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--address" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--status" />
                    {canSum ? (
                      <col className="contracts-page__colgrid contracts-page__colgrid--sum" />
                    ) : null}
                  </colgroup>
                <thead>
                  <tr>
                    <th className="sortable contracts-page__th-id" onClick={() => handleSort('id')} role="button">ID</th>
                    <th className="sortable contracts-page__th-contractor" onClick={() => handleSort('contractor')} role="button">Контрагент</th>
                    <th className="sortable contracts-page__th-number" onClick={() => handleSort('contract_number')} role="button">Номер договора</th>
                    <th className="sortable contracts-page__th-subject" onClick={() => handleSort('subject')} role="button">Предмет договора</th>
                    <th className="sortable contracts-page__th-address" onClick={() => handleSort('address')} role="button">Адрес</th>
                    <th className="sortable contracts-page__th-person" onClick={() => handleSort('manager')} role="button">Менеджер</th>
                    <th className="sortable contracts-page__th-person" onClick={() => handleSort('projectLead')} role="button">РП</th>
                    <th className="sortable contracts-page__th-date" onClick={() => handleSort('start_date')} role="button">Начало</th>
                    <th className="sortable contracts-page__th-date" onClick={() => handleSort('end_date')} role="button">Срок</th>
                    <th className="sortable contracts-page__th-status" onClick={() => handleSort('contract_status')} role="button">Статус</th>
                    {canSum ? (
                      <th className="sortable col-num contracts-page__th-sum" onClick={() => handleSort('total_amount')} role="button">Сумма</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedContracts.length === 0 ? (
                    <tr>
                      <td colSpan={canSum ? 11 : 10} className="data-table__empty">
                        Ничего не найдено
                      </td>
                    </tr>
                  ) : (
                    sortedContracts.map(({ contract, level, mainContractId, mainContractNumber }, index) => {
                    const prev = index > 0 ? sortedContracts[index - 1] : null;
                    const isNewMainContractGroup = !prev || prev.mainContractId !== mainContractId;
                    return (
                    <tr
                      key={contract.id}
                      className={`clickable-row${selectedContractId === contract.id ? ' data-table__row--selected' : ''} ${isNewMainContractGroup ? 'contracts-page__row--new-main' : ''} ${level === 2 ? 'contracts-page__row--supplementary' : 'contracts-page__row--main'}`}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          return;
                        }
                        setSelectedContractId(contract.id);
                      }}
                    >
                      <td className="contracts-page__td-id">
                        <button
                          type="button"
                          className="tasks-page__cell-link contracts-page__contract-id-text"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(contractDetailPath(level, mainContractId, contract.id));
                          }}
                        >
                          {formatContractRecordId(contract.id)}
                        </button>
                      </td>
                      <td className="contracts-page__td-contractor">
                        {level === 2 ? (
                          <span
                            className="contracts-page__tree-mark"
                            title={`Доп. соглашение к договору ${mainContractNumber}`}
                            aria-label={`Доп. соглашение к договору ${mainContractNumber}`}
                          >
                            └──
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="tasks-page__cell-link tasks-page__cell-link--block"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(contractDetailPath(level, mainContractId, contract.id));
                            }}
                          >
                            <span className="contracts-page__clamp-2">{getContractorName(contract.contractor_id)}</span>
                          </button>
                        )}
                      </td>
                      <td className="contracts-page__td-number">
                        <button
                          type="button"
                          className="tasks-page__cell-link tasks-page__cell-link--block"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(contractDetailPath(level, mainContractId, contract.id));
                          }}
                        >
                          <span className="contracts-page__clamp-2">
                            <strong>{contract.contract_number}</strong>
                          </span>
                        </button>
                      </td>
                      <td className="contracts-page__td-subject">
                        <button
                          type="button"
                          className="tasks-page__cell-link tasks-page__cell-link--name"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(contractDetailPath(level, mainContractId, contract.id));
                          }}
                        >
                          <span className="contracts-page__clamp-2">{contract.contract_subject || '—'}</span>
                        </button>
                      </td>
                      <td className="contracts-page__td-address">
                        <div className="contracts-page__clamp-2">
                          {level === 2 ? '—' : formatContractAddressCell(contract)}
                        </div>
                      </td>
                      <td className="contracts-page__td-person">
                        <div className="contracts-page__clamp-2">
                          {level === 2 ? '—' : getEmployeeName(contract.manager_id)}
                        </div>
                      </td>
                      <td className="contracts-page__td-person">
                        <div className="contracts-page__clamp-2">
                          {level === 2 ? '—' : getEmployeeName(contract.project_lead_id)}
                        </div>
                      </td>
                      <td className="contracts-page__td-date">{formatDateShort(contract.start_date)}</td>
                      <td className="contracts-page__td-date">{formatDateShort(contract.end_date)}</td>
                      <td className="contracts-page__td-status">
                        <div className="contracts-page__clamp-2">
                          {level === 2 ? '—' : contract.contract_status}
                        </div>
                      </td>
                      {canSum ? (
                        <td className="col-num contracts-page__td-sum">{formatContractSumDisplay(contract.total_amount)}</td>
                      ) : null}
                    </tr>
                  );
                  })
                  )}
                </tbody>
                </table>
              </div>
              <div className="table-panel__footer">
                <table className="data-table data-table--footer contracts-page__table">
                  <colgroup>
                    <col className="contracts-page__colgrid contracts-page__colgrid--id" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--contractor" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--number" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--subject" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--address" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--status" />
                    {canSum ? (
                      <col className="contracts-page__colgrid contracts-page__colgrid--sum" />
                    ) : null}
                  </colgroup>
                  <tfoot>
                    <tr>
                      <td>Итого: {sortedContracts.length}</td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      {canSum ? (
                        <td className="col-num">{formatContractSumDisplay(totalFilteredAmount)}</td>
                      ) : null}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
      </>
      )}
    </div>
  );
};

export default ContractsPage;
